import { Dispatch } from "redux";
import { getCookie } from "shared-utils/src/cookies";
import { IError, RootAction } from "../store";
import { addServerError } from "../store/global/actions";

export type ApiError = Error & { badRequest?: IError["badRequest"] };

const responseIsEmpty = (response: Response) => {
  const contentLength = response.headers.get("content-length");
  return (
    response.status === 204 ||
    (contentLength && parseInt(contentLength, 10) === 0)
  );
};

const responseIsJSON = (response: Response) => {
  const contentType = response.headers.get("content-type");
  return contentType && contentType.indexOf("application/json") !== -1;
};

// TODO - Type this better
interface IAsyncActions {
  request: any;
  failure: any;
  success: any;
}

interface IApiOptions {
  // When true, non-2xx responses (other than 400, which is always local) are
  // surfaced to the caller via the failure action and thrown error instead of
  // being dispatched to the global server error handler. Use for requests that
  // handle their own failures locally and not add a server error.
  handleErrorsLocally?: boolean;
}

async function _api<T>(
  url: string,
  dispatch: Dispatch<RootAction>,
  asyncActions: IAsyncActions,
  fetchArgs: RequestInit,
  successPayloadFormat?: {},
  options: IApiOptions = {},
): Promise<T> {
  let response;
  try {
    dispatch(asyncActions.request());
    response = await fetch(`/api/${url}`, {
      ...fetchArgs,
      credentials: "same-origin",
    });
  } catch (e: any) {
    // Network error
    const error: IError = { errorText: e.message, url };
    dispatch(asyncActions.failure(error));
    throw e;
  }
  if (!response.ok) {
    // Not 2xx
    const error: IError = { statusText: response.statusText, url };
    if (response.status === 400) {
      error.badRequest = await response.json();
    } else if (options.handleErrorsLocally) {
      // Caller opts to handle this failure locally, so skip the global server
      // error handler and just surface the error via the failure action/throw.
      // Parse the body so callers can inspect the error code.
      if (responseIsJSON(response) && !responseIsEmpty(response)) {
        try {
          error.badRequest = await response.json();
        } catch {
          // Malformed JSON — leave badRequest undefined and fall through to
          // the generic failure path below.
        }
      }
    } else {
      // Non 400 errors are handled globally
      dispatch(
        addServerError({
          code: response.status,
          details: await response.text(),
        }),
      );
    }
    dispatch(asyncActions.failure(error));
    const thrownError: ApiError = new Error(response.statusText);
    thrownError.badRequest = error.badRequest;
    throw thrownError;
  }
  const data = responseIsJSON(response)
    ? responseIsEmpty(response)
      ? {}
      : await response.json()
    : responseIsEmpty(response)
      ? ""
      : await response.json();
  dispatch(
    asyncActions.success(
      successPayloadFormat
        ? {
            ...successPayloadFormat,
            data,
          }
        : data,
    ),
  );
  return data as T;
}

export async function apiGet<T>(
  url: string,
  dispatch: Dispatch<RootAction>,
  asyncActions: IAsyncActions,
  successPayloadFormat?: {},
): Promise<T> {
  return _api(url, dispatch, asyncActions, {}, successPayloadFormat);
}

async function _apiDeletePostPut<T>(
  method: "DELETE" | "POST" | "PUT",
  url: string,
  data: object,
  dispatch: Dispatch<RootAction>,
  asyncActions: IAsyncActions,
  successPayloadFormat?: {},
  options?: IApiOptions,
): Promise<T> {
  return _api(
    url,
    dispatch,
    asyncActions,
    {
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCookie("csrftoken"),
      },
      method,
    },
    successPayloadFormat,
    options,
  );
}

export async function apiPost<T>(
  url: string,
  data: object,
  dispatch: Dispatch<RootAction>,
  asyncActions: IAsyncActions,
  successPayloadFormat?: {},
  options?: IApiOptions,
): Promise<T> {
  return _apiDeletePostPut<T>(
    "POST",
    url,
    data,
    dispatch,
    asyncActions,
    successPayloadFormat,
    options,
  );
}

export async function apiPut<T>(
  url: string,
  data: object,
  dispatch: Dispatch<RootAction>,
  asyncActions: IAsyncActions,
  successPayloadFormat?: {},
): Promise<T> {
  return _apiDeletePostPut<T>(
    "PUT",
    url,
    data,
    dispatch,
    asyncActions,
    successPayloadFormat,
  );
}

export async function apiDelete<T>(
  url: string,
  data: object,
  dispatch: Dispatch<RootAction>,
  asyncActions: IAsyncActions,
  successPayloadFormat?: {},
): Promise<T> {
  return _apiDeletePostPut<T>(
    "DELETE",
    url,
    data,
    dispatch,
    asyncActions,
    successPayloadFormat,
  );
}
